import { useState, useEffect, useRef, useCallback } from 'react'

import { getWordHash } from '@/utils/getWordHash'
import getVerificationStatus, { verificationStatuses } from '@/utils/getVerificationStatus'

export interface VerificationStatus {
  id: string
  color: string
  description: string
  label: string
  icon?: React.ReactNode
  chip?: React.ReactNode
  checked: boolean
}

export interface UsePhraseVerificationParams {
  targetText: string
  language: string
  onStatusChange?: (statusId: string, isChecked: boolean) => void
  verificationStatus?: keyof typeof verificationStatuses | null
  onCheckNow?: () => void
}

export interface UsePhraseVerificationReturn {
  status: VerificationStatus | null
  loading: boolean
  handleCheckNow: () => void
  currentHash: string | null
  dataInTooltip: string | null
}

export function usePhraseVerification({
  targetText,
  language,
  onStatusChange = () => null,
  verificationStatus = null,
  onCheckNow = () => null,
}: UsePhraseVerificationParams): UsePhraseVerificationReturn {
  const [status, setStatus] = useState<VerificationStatus | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [currentHash, setCurrentHash] = useState<string | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const targetHash = useRef<string | null>(null)
  const latestTargetHash = getWordHash(targetText, language)

  const fetchAndSetStatus = useCallback(async () => {
    setLoading(true)
    const newStatus = await getVerificationStatus(targetText, language)

    const hash = getWordHash(targetText, language)

    targetHash.current = hash
    setCurrentHash(hash)
    setStatus(newStatus)
    onStatusChange(newStatus.id, newStatus.checked)
    setLoading(false)
  }, [targetText, language, onStatusChange])

  useEffect(() => {
    if (!targetText) return

    // If a known status is passed, use it directly
    if (verificationStatus) {
      setStatus(verificationStatuses[verificationStatus])

      return
    }

    // Skip if hash hasn't changed
    if (targetHash.current === latestTargetHash) return

    // Debounce re-check
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    setStatus(null) // reset local status before new check
    timeoutRef.current = setTimeout(() => {
      fetchAndSetStatus()
    }, 2000)
  }, [latestTargetHash, verificationStatus])

  const handleCheckNow = useCallback(() => {
    onCheckNow()
    // Optional: You could force an immediate re-check here.
    // fetchAndSetStatus()
  }, [onCheckNow])

  return {
    status,
    loading,
    handleCheckNow,
    currentHash,
    dataInTooltip: status?.description ?? null,
  }
}
